import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Scrollbars } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import { type EventEmitter } from '@meitu/saas-editor-core/es/framework/services/events';
import {
    CanvasType,
    IBatchTemplateData,
    IExtraInfo,
    ILayerProps,
    PosterType,
    PPTDemoMode,
} from '@meitu/saas-editor-core/es/version-types';
import useClickOutside from '@/hooks/use-click-outside';
import { CollapseType } from '@/types';
import { BatchTemplate } from './components/batch-template';
import { CreateStageBtn } from './components/create-stage-btn';
import { MultiSelectProvider } from './components/multi-select-provider';
import { StageList } from './components/stage/stage-list';
import { NAV_SCROLL_EVENT, NAV_SCROLL_FRAME_EVENT } from './event-name';
import { useStageDrag } from './hooks/use-stage-drag';
import { useMultiSelectState, usePressToDelete } from './hooks/use-stage-event';
import './index.less';
import type { QuickStageOperate } from '@meitu/saas-editor-core/es/plugins/apps/user-editor/stage-operate';
import type { ClipboardPlugin } from '@meitu/saas-editor-core/es/plugins/clipboard';
import { type FontItem } from '@meitu/xdesign-widgets';

interface NavigationCollapseProps {
    editor: Editor;
    events: EventEmitter;
    onChange?: (source: number, destination: number) => void;
    onChangeBefore?: (source: number, destination: number) => void;
    setCurrentType: Function;
    currentType: CollapseType | null;
    recordId: string;
    setIsAiCreate: (isAiCreate: boolean) => void;
    setAiRegen: (data: { isAiRegen: boolean; regenIndex?: number }) => void;
    isAiCreate: boolean;
    initPPT: (stageIndex?: number) => void | Promise<void>;
    canvasType: CanvasType;
    posterType: PosterType;
    batchTemplateData: IBatchTemplateData | null;
    setBatchTemplateData: (data: any) => void;
    filterEmptyData: (batchTemplateData: IBatchTemplateData) => IBatchTemplateData;
    pptMode: PPTDemoMode | null;
    checkIsCover: (type: any) => boolean;
    closeAiPPTPrompt: () => void;
    stageExtraInfo?: { [key: string]: any };
    getCurrentScene: () => string;
    isFromHuaweiCustom: () => boolean;
    // 是否是图片编辑器
    isImageEditor?: boolean;
    transferIdParams: any;
    isFullscreen: boolean;
    templateSubject: string;
    showCreateTip: (tip: string) => void;
    hideCreateTip: () => void;
    transferTemplateToClient: (templateDetail: any, fontList: FontItem[]) => Promise<any>;
    packageTemplateConf: (
        templateConf: any,
        extraInfo: IExtraInfo,
        init: boolean,
    ) => {
        stages: ILayerProps[][];
        extraInfos: IExtraInfo[];
    };
    fetchPPTApplyItem: (params: any) => Promise<any>;
    fetchPPTCheckItemProcess: (params: any) => Promise<any>;
    fetchPPTItemDetail: (params: any) => Promise<any>;
    isAiPPT: boolean;
    isAiRegen: boolean;
    regenIndex: number;
    fetchAIPPTComponentList: (data: any) => Promise<any>;
    getStagePoints: (layers: ILayerProps[], page_type: string) => number;
    replaceComponentKit: (
        componentUrl: string,
        templateUrl: string,
        layers: ILayerProps[],
        fonts: any[],
        page_type: string,
        themeType: string,
    ) => Promise<any[]>;
}

const NavigationCollapse: React.FC<NavigationCollapseProps> = ({
    editor,
    events,
    setCurrentType,
    currentType,
    recordId,
    setIsAiCreate,
    setAiRegen,
    isAiCreate,
    initPPT,
    canvasType,
    posterType,
    batchTemplateData,
    setBatchTemplateData,
    filterEmptyData,
    checkIsCover,
    closeAiPPTPrompt,
    stageExtraInfo,
    getCurrentScene,
    isFromHuaweiCustom,
    isImageEditor,
    transferIdParams,
    isFullscreen,
    templateSubject,
    showCreateTip,
    hideCreateTip,
    transferTemplateToClient,
    packageTemplateConf,
    fetchPPTApplyItem,
    fetchPPTCheckItemProcess,
    fetchPPTItemDetail,
    isAiPPT,
    isAiRegen,
    regenIndex,
    fetchAIPPTComponentList,
    getStagePoints,
    replaceComponentKit,
}) => {
    const [isPickStage, setIsPickStage] = useState(editor.services.frame.getPickActiveFrame());
    const [isFocusInNavigation, setIsFocusInNavigation] = useState(false);

    // 监听激活舞台选中状态
    useEffect(() => {
        const handler = editor.services.frame.onPickActiveFrameChange((isPickStage) => {
            setIsPickStage(isPickStage);
        });
        return () => editor.off(handler);
    }, []);

    useEffect(() => {
        // 舞台快捷操作按钮点击事件
        const quickOperateEvent = editor
            .getInstance<QuickStageOperate>('QuickStageOperate')
            .onQuickClick((type: string) => {
                if (type) {
                    window._stat_track('hb_canvas_support_click', {
                        class: type,
                    });
                }

                if (type === 'delete_canvas') {
                    const stageIndex = editor.services.frame.getActiveIndex();
                    const extraInfo = editor.services.frame.getExtraInfo(stageIndex);
                    window._stat_track(
                        'tpbj_subfunction_tick',
                        isAiPPT
                            ? {
                                  type: '2',
                                  func: '19_93',
                                  page_type: extraInfo?.extraInfo?.label?.type || '',
                              }
                            : { func: '19_93' },
                    );
                }
            });
        return () => {
            editor.off(quickOperateEvent);
        };
    }, []);

    useEffect(() => {
        // 快捷键复制粘贴页面
        if (isImageEditor) return;
        // ATTENTION: 当前先保持旧的实现逻辑，后续需要跟产品确认下这块的交互逻辑
        return editor.getInstance<ClipboardPlugin>('ClipboardPlugin').onHotkeyEvent((keyType, isPage) => {
            if (!isPage) return;

            if (keyType === 'copy') {
                window._stat_track('tpbj_subfunction_click', {
                    func: '19_155',
                });
            } else if (keyType === 'paste') {
                setCurrentType(CollapseType.NAVIGATOR_MENU);
                handleAfterCopyOrDel(true);
                window._stat_track('tpbj_subfunction_tick', {
                    func: '19_155',
                });
            }
        });
    }, []);

    const handleDeleteTrack = () => {
        const stageIndex = editor.services.frame.getActiveIndex();
        const extraInfo = editor.services.frame.getExtraInfo(stageIndex);
        window._stat_track('tpbj_subfunction_click', {
            func: '19_93',
        });
        window._stat_track(
            'tpbj_subfunction_tick',
            isAiPPT
                ? {
                      type: '2',
                      func: '19_93',
                      page_type: extraInfo?.extraInfo?.label?.type || '',
                  }
                : { func: '19_93' },
        );
    };

    const { handleMultiSelect, handleAfterCopyOrDel, setSelection, clearSelection } = useMultiSelectState(editor);
    const { onDragging, onBeforeDragStart, moveCard } = useStageDrag(editor, events, setSelection);

    useEffect(() => {
        if (currentType !== CollapseType.NAVIGATOR_MENU) {
            clearSelection();
        }

        const handler = editor.services.frame.onActiveLayerDataUpdate((active) => {
            if (active) {
                const activeStageIndex = editor.services.frame.getActiveIndex();
                setSelection([activeStageIndex]);
            }
        });
        return () => editor.off(handler);
    }, [currentType]);

    usePressToDelete({
        editor,
        handleAfterCopyOrDel,
        isPickStage,
        isFocusInNavigation,
        cb: handleDeleteTrack,
    });

    const onScrollFrame = useCallback(() => {
        // console.log('onScrollFrame', scrollBarRef.current);
        events.emit(NAV_SCROLL_FRAME_EVENT, scrollBarRef.current);
    }, []) as any;

    const scrollBarRef = useRef();

    // 点击导航栏外部, 设置焦点不在导航栏
    const navigationWrapperRef = useClickOutside(() => {
        setIsFocusInNavigation(false);
    });

    // 点击导航栏内部, 设置焦点在导航栏
    useEffect(() => {
        const handler = () => {
            setIsFocusInNavigation(true);
        };
        document.addEventListener('mouseup', handler);
        return () => document.removeEventListener('mouseup', handler);
    }, [isFocusInNavigation]);

    const onNavScroll = () => {
        events.emit(NAV_SCROLL_EVENT);
    };

    return (
        <div className="c-navigation-collapse" ref={navigationWrapperRef}>
            <MultiSelectProvider
                value={{
                    handleMultiSelect,
                    handleDeleteTrack,
                    handleAfterCopyOrDel,
                }}
            >
                <CreateStageBtn
                    editor={editor}
                    events={events}
                    recordId={recordId}
                    initPPT={initPPT}
                    canvasType={canvasType}
                    posterType={posterType}
                    setIsAiCreate={setIsAiCreate}
                    setAiRegen={setAiRegen}
                    isAiCreate={isAiCreate}
                    getCurrentScene={getCurrentScene}
                    isFromHuaweiCustom={isFromHuaweiCustom}
                    transferIdParams={transferIdParams}
                    isFullscreen={isFullscreen}
                    templateSubject={templateSubject}
                    showCreateTip={showCreateTip}
                    hideCreateTip={hideCreateTip}
                    transferTemplateToClient={transferTemplateToClient}
                    packageTemplateConf={packageTemplateConf}
                    fetchPPTApplyItem={fetchPPTApplyItem}
                    fetchPPTCheckItemProcess={fetchPPTCheckItemProcess}
                    fetchPPTItemDetail={fetchPPTItemDetail}
                    stageExtraInfo={stageExtraInfo}
                />
                <BatchTemplate
                    events={events}
                    editor={editor}
                    filterEmptyData={filterEmptyData}
                    batchTemplateData={batchTemplateData}
                    setBatchTemplateData={setBatchTemplateData}
                />
                <Scrollbars
                    scrollbarsRef={scrollBarRef as any}
                    onScrollFrame={onScrollFrame}
                    autoHide={true}
                    onScroll={onNavScroll}
                    className={classNames('c-navigation-collapse__list', {
                        onDragging,
                    })}
                >
                    <DragDropContext onDragEnd={moveCard} onBeforeDragStart={onBeforeDragStart}>
                        <Droppable droppableId="list">
                            {(provided) => (
                                <div ref={provided.innerRef} {...provided.droppableProps}>
                                    <StageList
                                        editor={editor}
                                        events={events}
                                        posterType={posterType}
                                        canvasType={canvasType}
                                        checkIsCover={checkIsCover}
                                        closeAiPPTPrompt={closeAiPPTPrompt}
                                        stageExtraInfo={stageExtraInfo}
                                        isAiPPT={isAiPPT}
                                        isAiRegen={isAiRegen}
                                        initPPT={initPPT}
                                        regenIndex={regenIndex}
                                        transferIdParams={transferIdParams}
                                        isFromHuaweiCustom={isFromHuaweiCustom}
                                        getCurrentScene={getCurrentScene}
                                        isFullscreen={isFullscreen}
                                        showCreateTip={showCreateTip}
                                        hideCreateTip={hideCreateTip}
                                        transferTemplateToClient={transferTemplateToClient}
                                        packageTemplateConf={packageTemplateConf}
                                        recordId={recordId}
                                        setIsAiCreate={setIsAiCreate}
                                        setAiRegen={setAiRegen}
                                        fetchPPTApplyItem={fetchPPTApplyItem}
                                        fetchPPTCheckItemProcess={fetchPPTCheckItemProcess}
                                        fetchPPTItemDetail={fetchPPTItemDetail}
                                        templateSubject={templateSubject}
                                        fetchAIPPTComponentList={fetchAIPPTComponentList}
                                        getStagePoints={getStagePoints}
                                        replaceComponentKit={replaceComponentKit}
                                    />
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Scrollbars>
            </MultiSelectProvider>
        </div>
    );
};

export default React.memo(NavigationCollapse);
